import React from 'react';
import churchForms from 'src/services/api/churchForms';
import {
  MAX_CATALOG_IMAGES,
  catalogImagesFromRecord,
  jobPostingsFromRecord,
  mergeEditingInputs,
  yesNoValue,
} from 'src/containers/comunidad-network/form/networkFormUtils';

const SKIP_KEYS = new Set([
  'Guest',
  'Seminaries',
  'logo',
  'catalogImages',
  'published',
  'captcha',
]);

export const catalogItemsFromUrls = (urls = []) =>
  urls.map((url) => ({
    key: url,
    url,
    preview: url,
  }));

export const buildNetworkFormData = ({
  inputs,
  captcha,
  logoFile,
  logoPreview,
  catalogItems,
  includePublished = false,
  extraFields = {},
}) => {
  const payload = new FormData();

  Object.entries(inputs).forEach(([key, value]) => {
    if (SKIP_KEYS.has(key) || value == null || typeof value === 'object') return;
    payload.append(key, String(value));
  });
  Object.entries(extraFields).forEach(([key, value]) => {
    if (value != null) payload.append(key, String(value));
  });

  if (captcha) payload.append('captcha', captcha);

  if (includePublished) {
    const published = yesNoValue(inputs.published);
    if (published) payload.append('published', published);
  }

  if (logoFile) payload.append('logo', logoFile, 'logo.jpg');

  const keptCatalog = [];
  catalogItems.forEach((item, index) => {
    if (item.file) {
      payload.append('catalog', item.file, `catalog-${index + 1}.jpg`);
    } else if (item.url) {
      keptCatalog.push(item.url);
    }
  });

  const keptLogo = !logoFile && /^https?:\/\//.test(logoPreview) ? logoPreview : '';
  payload.append('existingLogo', keptLogo);
  payload.append('existingCatalog', JSON.stringify(keptCatalog));
  payload.append(
    'jobPostings',
    JSON.stringify(
      jobPostingsFromRecord(inputs.jobPostings)
        .filter((posting) => posting.title.trim() || posting.description.trim())
        .map(({ id, title, description }) => ({
          id,
          title: title.trim(),
          description: description.trim(),
        }))
    )
  );

  return payload;
};

export const submitNetworkCompany = async ({
  captchaRequired,
  getCaptchaToken,
  formData,
  editing = false,
  revision = false,
}) => {
  const captcha = await getCaptchaToken({ skip: !captchaRequired });
  if (captchaRequired && !captcha) return { captchaFailed: true };

  const payload = buildNetworkFormData({
    ...formData,
    captcha: captchaRequired ? captcha : undefined,
  });
  const response = revision
    ? await churchForms.postComunidadNetworkRevision(payload)
    : await churchForms.postComunidadNetwork(payload, editing);
  return { response };
};

export const useNetworkCompanyForm = () => {
  const [inputs, setInputs] = React.useState({});
  const [errorInputs, setErrorInputs] = React.useState({});
  const [sending, setSending] = React.useState(false);
  const [logoFile, setLogoFile] = React.useState(null);
  const [logoPreview, setLogoPreview] = React.useState('');
  const [catalogItems, setCatalogItems] = React.useState([]);

  const clearMedia = React.useCallback(() => {
    setLogoFile(null);
    setLogoPreview('');
    setCatalogItems([]);
  }, []);

  const handleInputs = (event) => {
    const { value, name } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const selectHandle = (_event, { value, name }) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const setJobPostings = (jobPostings) => {
    setInputs((prev) => ({ ...prev, jobPostings }));
  };

  const loadRecord = React.useCallback((record = {}) => {
    const nextInputs = mergeEditingInputs(record);
    setInputs(nextInputs);
    setLogoFile(null);
    setLogoPreview(nextInputs.logo || '');
    setCatalogItems(catalogItemsFromUrls(catalogImagesFromRecord(nextInputs.catalogImages)));
  }, []);

  const reset = React.useCallback(() => {
    setInputs({});
    setErrorInputs({});
    clearMedia();
  }, [clearMedia]);

  const media = {
    logoPreview,
    catalogItems,
    onLogoSelected: (file, preview) => {
      setLogoFile(file);
      setLogoPreview(preview);
    },
    onLogoClear: () => {
      setLogoFile(null);
      setLogoPreview('');
    },
    onCatalogSelected: (items) =>
      setCatalogItems((prev) => [...prev, ...items].slice(0, MAX_CATALOG_IMAGES)),
    onCatalogRemove: (key) =>
      setCatalogItems((prev) => prev.filter((item) => (item.key || item.url) !== key)),
  };

  return {
    inputs,
    setInputs,
    errorInputs,
    setErrorInputs,
    sending,
    setSending,
    logoFile,
    logoPreview,
    catalogItems,
    clearMedia,
    handleInputs,
    selectHandle,
    setJobPostings,
    loadRecord,
    reset,
    media,
  };
};
